import { useEffect, useMemo, useRef, useState } from 'react'
import { AlertTriangle, Info } from 'lucide-react'
import { apiGet, cachedGet, postJson } from '../../lib/api.js'
import { asList } from '../../lib/chart-helpers.js'
import {
  Drawer, DrawerFooter, Field, Input, Textarea, SearchSelect, BranchSelect,
  useToast, bkkTodayISO, fmtTime, DECISIONS,
} from '../../kit'

/**
 * "เพิ่มการรักษา" — create a visit that has no appointment behind it (a walk-in,
 * or a visit nobody booked).
 *
 * Four keys and two notes; everything else Xano fills in. The write goes to
 * POST /api/treatment-manual, which stamps employee/creator from the session.
 *
 * ONE CHART PER VISIT: Xano keys the chart on patient + date + doctor + branch,
 * so if this patient already has a treatment with that doctor at that branch on
 * that day, the new treatment JOINS its chart instead of making a second one.
 * The drawer says which happened, because "where did my chart go" is otherwise a
 * support call.
 *
 * FIELD ORDER IS THE ANSWER ORDER: วันที่ → สาขา → หมอ. Who the doctor can be is a
 * function of the other two — a walk-in was seen by someone who was actually in that
 * branch on that day — so asking for the doctor first left staff scrolling 600 names
 * with nothing narrowing them.
 *
 * THE TIMETABLE IS A GATE, NOT A SORT (Ram, 2026-09-23). The picker offers ONLY the
 * doctors holding a shift at that branch on that date, each with their window; there
 * is no "หมออื่น" fallback and no override. An empty timetable therefore blocks the
 * form on purpose — the fix is to enter the shift, not to record a visit against a
 * doctor who was not there. That also keeps the chart key (patient + date + doctor +
 * branch) honest, since a doctor picked off-schedule opens a chart nobody can explain.
 */
// Timetable rows carry epoch-ms; the shift hint reads in clinic time (kit
// fmtTime is Bangkok HH:mm whatever the browser's clock says).
const bkkHM = (ms) => fmtTime(Number(ms))


export default function AddTreatmentDrawer({ patientId, defaultBranchId, branches = [], onClose, onCreated }) {
  const [doctors, setDoctors] = useState(null)
  const [types, setTypes] = useState(null)
  const [shift, setShift] = useState(null)      // timetable rows for (date, branch)
  const [shiftLoading, setShiftLoading] = useState(false)
  const [shiftErr, setShiftErr] = useState(false)
  const [shiftTry, setShiftTry] = useState(0)   // retry ticker for the timetable fetch
  // The clinic's day, not the browser's.
  const [date, setDate] = useState(() => bkkTodayISO())
  const [doctorId, setDoctorId] = useState('')
  // Seeded from the patient's latest visit — but never as สนามเป้า (head office,
  // decision #2): the form does not offer it, so a seeded 1 would be submitted
  // invisibly. Such a patient starts with the branch unchosen instead.
  const [branchId, setBranchId] = useState(() => (
    defaultBranchId && Number(defaultBranchId) !== DECISIONS.hqBranchId ? String(defaultBranchId) : ''
  ))
  const [typeName, setTypeName] = useState('')
  const [alId, setAlId] = useState('')
  const [detail, setDetail] = useState('')
  const [remark, setRemark] = useState('')
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState('')
  const inflight = useRef(false)
  const toast = useToast()
  // What it opened with, so `dirty` means "the user typed something" — the kit
  // Drawer then asks before a stray backdrop click throws it away.
  const initial = useRef({ date, branchId })

  useEffect(() => {
    cachedGet('/api/doctors', { slim: 1 }).then((r) => setDoctors(asList(r))).catch(() => setDoctors([]))
    cachedGet('/api/appointment-types').then((r) => setTypes(asList(r))).catch(() => setTypes([]))
  }, [])

  // Who was on shift at this branch on this day. Same source the booking calendar's
  // month view reads, windowed to the one day. Because this list is now the ONLY way
  // to name a doctor, a failed fetch must NOT read as "nobody worked that day" — the
  // two look identical on screen and the second one blames the schedule for a network
  // blip. It gets its own state and its own retry.
  useEffect(() => {
    if (!date || !branchId) { setShift(null); setShiftErr(false); setShiftLoading(false); return }
    let alive = true
    setShiftLoading(true); setShiftErr(false)
    apiGet('/api/appointments', { type: 'timetable', branch_id: branchId, start_date: date, end_date: date })
      .then((r) => { if (alive) { setShift(asList(r)); setShiftLoading(false) } })
      .catch(() => { if (alive) { setShift(null); setShiftErr(true); setShiftLoading(false) } })
    return () => { alive = false }
  }, [date, branchId, shiftTry])

  // `inverse-background` rows ARE the working windows; `block: true` is a break
  // inside one and a real appointment has no event_type at all.
  const onShift = useMemo(() => {
    const m = new Map()
    for (const a of shift || []) {
      if (a.event_type !== 'inverse-background' || a.block === true) continue
      const did = String(a.doctor_id)
      if (!m.has(did)) m.set(did, { start: Infinity, windows: [] })
      const rec = m.get(did)
      rec.start = Math.min(rec.start, Number(a.start_date))
      rec.windows.push(`${bkkHM(a.start_date)}–${bkkHM(a.end_date)}`)
    }
    return m
  }, [shift])

  // ประเภท → รายละเอียด, the same two-step the appointment form uses so staff
  // pick the appointmentList row they already know by name.
  const byType = useMemo(() => {
    const m = new Map()
    for (const ty of types || []) {
      const k = ty.type || 'อื่นๆ'
      if (!m.has(k)) m.set(k, [])
      m.get(k).push({ id: String(ty.id), detail: ty.detail || k })
    }
    return m
  }, [types])
  const typeNames = useMemo(() => [...byType.keys()].sort((a, b) => a.localeCompare(b, 'th')), [byType])
  const details = byType.get(typeName) || []

  // Only the doctors on shift, in the order the day ran. No group headers — one
  // bucket needs no heading, and a heading would imply a second bucket exists.
  const docOpts = useMemo(() => {
    const out = []
    for (const d of doctors || []) {
      const shiftRec = onShift.get(String(d.id))
      if (!shiftRec) continue
      const label = d.code || [d.name, d.surname].filter(Boolean).join(' ') || `หมอ #${d.id}`
      out.push({
        value: String(d.id),
        label,
        hint: shiftRec.windows.join(', '),
        // Searchable but not shown: staff look a doctor up by นามสกุล, ชื่อเล่น or the
        // English name, none of which fit on the row beside the shift window.
        search: [d.name, d.surname, d.nickname, d.name_eng, d.license].filter(Boolean).join(' '),
        _start: shiftRec.start,
      })
    }
    return out.sort((a, b) => (a._start - b._start) || a.label.localeCompare(b.label, 'th'))
  }, [doctors, onShift])

  // A doctor chosen before the date or branch moved may not be on the NEW day's
  // timetable. Silently keeping them would submit exactly the off-schedule doctor
  // the gate exists to refuse, so the field clears itself and asks again.
  useEffect(() => {
    if (!doctorId) return
    if (doctors === null || shiftLoading || !date || !branchId) return
    if (!onShift.has(doctorId)) setDoctorId('')
  }, [doctorId, doctors, onShift, shiftLoading, date, branchId])

  const noShift = !!date && !!branchId && !shiftLoading && !shiftErr && shift !== null && !docOpts.length
  const doctorPlaceholder = doctors === null ? 'กำลังโหลด…'
    : !date || !branchId ? 'เลือกวันที่และสาขาก่อน'
    : shiftLoading ? 'กำลังดูตารางหมอ…'
    : shiftErr ? 'ดึงตารางหมอไม่สำเร็จ'
    : noShift ? 'ไม่มีหมอลงตารางวันนี้'
    : '— เลือกหมอ —'

  const valid = !!(date && doctorId && branchId && alId)
  const dirty = !!(doctorId || typeName || alId || detail || remark
    || date !== initial.current.date || branchId !== initial.current.branchId)

  async function submit() {
    if (!valid || inflight.current) return
    inflight.current = true
    setSaving(true); setErr('')
    try {
      const res = await postJson('/api/treatment-manual', {
        patient_id: Number(patientId),
        date,
        doctor_id: Number(doctorId),
        branch_id: Number(branchId),
        appointmentlist_id: Number(alId),
        detail,
        remark,
      })
      onCreated?.(res)
    } catch (e) {
      const msg = humanError(e)
      setErr(msg)
      toast.error('บันทึกไม่สำเร็จ', { detail: msg })
    } finally {
      inflight.current = false
      setSaving(false)
    }
  }

  return (
    <Drawer
      onClose={onClose}
      width="sm"
      title="เพิ่มการรักษา"
      busy={saving}
      dirty={dirty}
      footer={(
        <DrawerFooter
          onCancel={onClose}
          onSave={submit}
          saving={saving}
          canSave={valid}
          saveLabel="สร้างการรักษา"
          status={valid ? null : 'กรอกข้อมูลที่จำเป็นให้ครบ'}
        />
      )}
    >
      <div className="rounded-2xl bg-white border border-slate-200 p-4 space-y-4">
        <p className="text-[13px] text-slate-500">
          สำหรับเคสที่ไม่ได้มาจากนัดหมาย เช่น walk-in — ถ้ามาจากนัด ให้กด “สร้างการรักษา” ที่หน้านัดหมายแทน
          เพื่อให้การรักษาผูกกับนัดนั้น
        </p>

        <Field label="วันที่" required>
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>

        {/* Kit form branches: สนามเป้า (head office) is never offered. */}
        <Field label="สาขา" required>
          <BranchSelect mode="form" branches={branches} value={branchId}
            onChange={(v) => setBranchId(v === '' || v == null ? '' : String(v))}
            placeholder="— เลือกสาขา —" />
        </Field>

        <Field
          label="หมอ"
          required
          hint="เลือกได้เฉพาะหมอที่ลงตารางที่สาขานี้ในวันที่เลือก"
          // A failed fetch must not read as "nobody worked that day" — its own
          // message and its own retry.
          error={shiftErr ? (
            <span className="inline-flex items-start gap-1.5">
              <AlertTriangle size={13} className="flex-none mt-0.5" />
              <span>
                ดึงตารางหมอไม่สำเร็จ{' '}
                <button type="button" onClick={() => setShiftTry((t) => t + 1)}
                  className="font-semibold underline underline-offset-2 hover:text-rose-800">ลองใหม่</button>
              </span>
            </span>
          ) : undefined}
        >
          {({ id }) => (
            <>
              <SearchSelect
                id={id}
                options={docOpts}
                value={doctorId}
                onChange={setDoctorId}
                disabled={doctors === null || !date || !branchId || shiftLoading || !docOpts.length}
                placeholder={doctorPlaceholder}
                searchPlaceholder="พิมพ์ชื่อหมอ…"
                emptyText="ไม่พบหมอที่ค้นหา"
              />
              {noShift && (
                // A dead end with no explanation reads as a broken form. Name the cause
                // and the fix, because the fix is on the schedule page, not here.
                <span className="mt-1.5 flex items-start gap-1.5 text-[12.5px] text-amber-700">
                  <AlertTriangle size={13} className="flex-none mt-0.5" />
                  ไม่มีหมอลงตารางที่สาขานี้ในวันที่เลือก — ตรวจสอบวันที่และสาขา หรือลงตารางหมอให้เรียบร้อยก่อน
                </span>
              )}
            </>
          )}
        </Field>

        <Field label="ประเภทการรักษา" required>
          <SearchSelect value={typeName} onChange={(v) => { setTypeName(v); setAlId('') }}
            options={typeNames.map((t) => ({ value: t, label: t }))} disabled={types === null}
            placeholder={types === null ? 'กำลังโหลด…' : '— เลือกประเภท —'} searchPlaceholder="พิมพ์ชื่อประเภท…" />
        </Field>

        {typeName && (
          <Field label="รายละเอียดประเภท" required>
            <SearchSelect value={alId} onChange={setAlId}
              options={details.map((d) => ({ value: d.id, label: d.detail }))}
              placeholder="— เลือกรายละเอียด —" />
          </Field>
        )}

        <Field label="รายละเอียด">
          <Textarea rows={2} value={detail} onChange={(e) => setDetail(e.target.value)} className="resize-y" />
        </Field>

        <Field label="หมายเหตุภายใน">
          <Textarea rows={2} value={remark} onChange={(e) => setRemark(e.target.value)} className="resize-y"
            placeholder="ไม่แสดงต่อคนไข้ — พนักงานและทันตแพทย์เห็น" />
        </Field>

        <div className="flex items-start gap-1.5 text-[12.5px] text-slate-500">
          <Info size={13} className="flex-none mt-0.5 text-slate-400" />
          <span>
            ถ้าคนไข้มีการรักษากับหมอคนนี้ ที่สาขานี้ ในวันเดียวกันอยู่แล้ว ระบบจะใช้ chart เดิมของวันนั้น
            ไม่สร้าง chart ใหม่
          </span>
        </div>

        {err && (
          <div className="flex items-start gap-1.5 rounded-xl bg-rose-50 border border-rose-200 p-3 text-[13px] text-rose-700" role="alert">
            <AlertTriangle size={14} className="flex-none mt-0.5" />{err}
          </div>
        )}
      </div>
    </Drawer>
  )
}

// api.js throws `HTTP <status>: <body>`; dig the Thai sentence back out.
function humanError(e) {
  const raw = String(e?.message || '')
  if (e?.status === 403) return 'บัญชีนี้ไม่มีสิทธิ์สร้างการรักษา'
  const m = raw.match(/\{[\s\S]*\}\s*$/)
  if (m) {
    try {
      const j = JSON.parse(m[0])
      const msg = j.message || j.detail || j.error
      if (msg) return String(msg)
    } catch { /* not JSON */ }
  }
  return raw || 'สร้างการรักษาไม่สำเร็จ'
}
